import React, { useState, useCallback } from 'react';
import FileUpload from './FileUpload';
import FileList from './FileList';
import ProgressBar from './ProgressBar';
import { protectPdf } from '../services/pdfService';
import { DownloadIcon, ProtectIcon, SpinnerIcon, ErrorIcon } from './icons';
import { useTranslation } from '../i18n';

const ProtectTool: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const { t } = useTranslation();

  const handleFileSelect = useCallback((selectedFiles: File[]) => {
    if (selectedFiles.length > 0) {
      setFile(selectedFiles[0]);
      setError(null);
      setResultUrl(null);
    }
  }, []);

  const handleRemoveFile = useCallback(() => {
    setFile(null);
  }, []);

  const handleProtect = async () => {
    if (!file) {
      setError(t('errorSelectFileToProtect'));
      return;
    }
    if (!password) {
      setError(t('errorPasswordRequired'));
      return;
    }
    setIsLoading(true);
    setIsProcessing(false);
    setProgress(0);
    setError(null);
    setResultUrl(null);
    try {
      const protectedBlob = await protectPdf(file, password, setProgress);
      setIsProcessing(true);
      const url = URL.createObjectURL(protectedBlob);
      setResultUrl(url);
    } catch (err: any) {
      setError(err.message || t('errorUnexpected'));
    } finally {
      setIsLoading(false);
      setIsProcessing(false);
    }
  };

  return (
    <div className="w-full max-w-2xl mx-auto bg-slate-800/50 rounded-2xl shadow-xl p-6 sm:p-8 border border-slate-700">
      <FileUpload onFilesSelect={handleFileSelect} multiple={false} accept=".pdf" />
      
      {file && <FileList files={[file]} onRemoveFile={handleRemoveFile} />}

      {file && (
        <div className="mt-6">
          <label htmlFor="password-input" className="block text-sm font-medium text-slate-300 mb-2">
            {t('setPasswordLabel')}
          </label>
          <input
            id="password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('passwordPlaceholder')}
            className="w-full bg-slate-700 border border-slate-600 rounded-lg py-2.5 px-3 text-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
          />
        </div>
      )}

      {isLoading && <ProgressBar progress={progress} isProcessing={isProcessing} />}
      
      <div className="mt-6">
        <button
          onClick={handleProtect}
          disabled={isLoading || !file || !password}
          className="w-full flex items-center justify-center gap-3 bg-indigo-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed transition-all duration-200"
        >
          {isLoading ? <SpinnerIcon /> : <ProtectIcon />}
          <span>{isLoading ? (isProcessing ? t('statusProcessing') : t('statusUploading')) : t('protectButton')}</span>
        </button>
      </div>

      {error && (
         <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg flex items-center gap-3">
            <ErrorIcon/>
            <span>{error}</span>
        </div>
      )}
      {resultUrl && (
        <div className="mt-4 p-4 bg-green-500/10 border border-green-500/30 text-green-300 rounded-lg">
          <h3 className="font-semibold mb-2">{t('successTitle')}</h3>
          <p className="text-sm mb-4">{t('successProtectMessage')}</p>
          <a
            href={resultUrl}
            download={file?.name.replace(/\.pdf$/i, '_protected.pdf') || 'protected.pdf'}
            className="flex w-full items-center justify-center gap-3 bg-green-600 text-white font-bold py-3 px-4 rounded-lg hover:bg-green-700 transition-all duration-200"
          >
            <DownloadIcon/>
            {t('downloadProtectedButton')}
          </a>
        </div>
      )}
    </div>
  );
};

export default ProtectTool;
